import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, keyframes, } from '@emotion/react';
import { memo, useMemo, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { types } from './constants';
const circularRotateKeyframe = keyframes `
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;
/*
Not sure why we need these specific values, but they make the Loader animate correctly.

References:
- https://css-tricks.com/building-progress-ring-quickly/#as-a-react-component
- https://www.webtips.dev/how-to-recreate-the-material-circular-loader-in-css
- https://github.com/mui-org/material-ui/blob/master/packages/mui-material/src/CircularProgress/CircularProgress.js
*/
const circularDashKeyframe = keyframes `
  0% {
    stroke-dasharray: 5, 220;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -130;
  }
`;
const strokeWidth = 3;
const radius = 24;
const normalizedRadius = radius - (strokeWidth / 2);
const circumference = normalizedRadius * 2 * Math.PI;
const BaseLoader = ({ className, strokeBackgroundColor, strokeColor, textColor, type, value = 0, }) => {
    const svgStyles = useMemo(() => (css `
        ${type === types.indeterminate && css `animation: ${circularRotateKeyframe} 2s linear infinite;`}
        display: flex;
        position: absolute;
      `), [
        type,
    ]);
    const containerStyles = useMemo(() => (css `
        display: block;
        position: relative;
      `), []);
    const baseCircleStyles = useMemo(() => (css `
        cx: ${styleUtils.rem(radius)};
        cy: ${styleUtils.rem(radius)};
        fill: transparent;
        r: ${styleUtils.rem(normalizedRadius)};
        stroke: ${strokeColor};
        stroke-dasharray: ${circumference + ' ' + circumference};
        stroke-width: ${strokeWidth};
        transition: stroke-dashoffset 0.35s;
        transform: rotate(-90deg);
        transform-origin: 50% 50%;
      `), [
        strokeColor,
    ]);
    const circleStyles = useMemo(() => (css `
        ${baseCircleStyles}
        ${type === types.indeterminate && css `animation: ${circularDashKeyframe} 1.5s ease-in-out infinite;`}
        ${type === types.determinate && css `stroke-dashoffset: ${circumference - value / 100 * circumference};`}
      `), [
        baseCircleStyles,
        type,
        value,
    ]);
    const backgroundCircleStyles = useMemo(() => (css `
        ${baseCircleStyles}
        stroke: ${strokeBackgroundColor};
      `), [
        baseCircleStyles,
        strokeBackgroundColor,
    ]);
    const valueStyles = useMemo(() => (css `
        ${theme.typeStyles.titleExtraSmallStyles}
        align-items: center;
        color: ${textColor};
        display: flex;
        height: 100%;
        justify-content: center;
        position: absolute;
        width: 100%;
      `), [
        textColor,
    ]);
    return (_jsxs("span", Object.assign({ className: className, css: containerStyles }, { children: [_jsxs("svg", Object.assign({ css: svgStyles, viewBox: `0 0 ${radius * 2} ${radius * 2}` }, { children: [_jsx("circle", { css: backgroundCircleStyles }), _jsx("circle", { css: circleStyles })] })), type === types.determinate
                && (_jsxs("span", Object.assign({ css: valueStyles }, { children: [value, "%"] })))] })));
};
const MemoizedBaseLoader = memo(BaseLoader);
export { MemoizedBaseLoader as BaseLoader };
// MC-1068 Temporary hack for Storybook.
export default MemoizedBaseLoader;
